import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Plus, CheckCircle2, Building2, Loader2, RefreshCw, Search, RotateCw, ScanLine } from "lucide-react";

export default function Clients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", company: "", email: "", address: "", phone: "" });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingId, setCreatingId] = useState(null);
  const [scanningCard, setScanningCard] = useState(false);
  const cardFileRef = useRef();

  const handleCardFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setScanningCard(true);
      try {
        const { data } = await api.post("/scan-client-card", { image_base64: reader.result });
        setForm((f) => ({
          name: data.name || f.name,
          company: data.company || f.company,
          email: data.email || f.email,
          address: data.address || f.address,
          phone: data.phone || f.phone,
        }));
        toast.success("Document reconnu — vérifiez les informations");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de la reconnaissance");
      } finally {
        setScanningCard(false);
        if (cardFileRef.current) cardFileRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  const refreshCache = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/bexio/refresh-cache");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const createInBexio = async (id) => {
    setCreatingId(id);
    try {
      const { data } = await api.post(`/clients/${id}/create-in-bexio`);
      toast.success("Client créé dans Bexio", { description: data.message });
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreatingId(null);
    }
  };

  const syncBexio = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/bexio/sync-contacts");
      toast.success(data.message);
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const load = async (f) => {
    const params = f && f !== "all" ? { status: f } : {};
    const { data } = await api.get("/clients", { params });
    setClients(data);
  };
  useEffect(() => { load(filter); }, [filter]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client créé");
      setForm({ name: "", company: "", email: "", address: "", phone: "" });
      setOpen(false);
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const createAndBexio = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    setSaving(true);
    try {
      const { data: newClient } = await api.post("/clients", form);
      const { data } = await api.post(`/clients/${newClient.id}/create-in-bexio`);
      toast.success("Client créé dans Bexio", { description: data.message });
      setForm({ name: "", company: "", email: "", address: "", phone: "" });
      setOpen(false);
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load(filter);
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    try {
      await api.post(`/clients/${id}/activate`);
      toast.success("Client marqué comme créé dans Bexio");
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? clients.filter((c) => `${c.name} ${c.company || ""}`.toLowerCase().includes(q))
    : clients;

  return (
    <div className="p-6 md:p-12" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5E2129] mb-2">Répertoire</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">Clients</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="sync-bexio-btn" onClick={syncBexio} disabled={syncing} variant="outline" className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5 px-5 py-3 h-auto">
            {syncing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <RefreshCw className="w-5 h-5 mr-2" />} Synchroniser Bexio
          </Button>
          <Button data-testid="refresh-cache-btn" onClick={refreshCache} disabled={refreshing} variant="outline" className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5 px-5 py-3 h-auto">
            {refreshing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <RotateCw className="w-5 h-5 mr-2" />} Rafraîchir cache
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" className="rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white px-6 py-3 h-auto">
              <Plus className="w-5 h-5 mr-2" /> Nouveau client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white" data-testid="add-client-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Nouveau client</DialogTitle>
              <DialogDescription>Scannez un document ou saisissez les informations du client.</DialogDescription>
            </DialogHeader>

            <input ref={cardFileRef} type="file" accept="image/*" capture="environment" onChange={handleCardFile} className="hidden" data-testid="client-card-file-input" />
            <button
              type="button"
              data-testid="scan-client-doc-btn"
              onClick={() => cardFileRef.current?.click()}
              disabled={scanningCard}
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#EBE5D9] hover:border-[#5E2129]/40 bg-[#FDFBF7] py-4 text-sm font-medium text-[#5E2129] transition-colors disabled:opacity-60"
            >
              {scanningCard ? <><Loader2 className="w-5 h-5 animate-spin" /> Reconnaissance en cours…</> : <><ScanLine className="w-5 h-5" /> Scanner un document (carte de visite, en-tête…)</>}
            </button>

            <div className="space-y-4 py-2">
              {[
                { k: "name", l: "Nom *", p: "Jean Dupont" },
                { k: "company", l: "Entreprise", p: "Dupont SA" },
                { k: "email", l: "Email", p: "jean@dupont.ch" },
                { k: "phone", l: "Téléphone", p: "+41 32 555 12 34" },
                { k: "address", l: "Adresse", p: "Rue du Lac 12, 1003 Lausanne" },
              ].map((f) => (
                <div key={f.k}>
                  <Label className="text-xs uppercase tracking-wider text-[#5E2129] font-semibold">{f.l}</Label>
                  <Input data-testid={`client-${f.k}`} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.p} className="mt-2 bg-[#FDFBF7] border-[#EBE5D9]" />
                </div>
              ))}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" data-testid="save-client-btn" onClick={create} disabled={saving} className="rounded-full border-[#5E2129]/30 text-[#5E2129] hover:bg-[#5E2129]/5">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer (en attente)"}
              </Button>
              <Button data-testid="save-client-bexio-btn" onClick={createAndBexio} disabled={saving} className="rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Building2 className="w-4 h-4 mr-2" /> Créer dans Bexio</>}
              </Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="w-4 h-4 text-[#666] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="clients-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un client…"
          className="pl-9 bg-white border-[#EBE5D9]"
        />
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-[#F1EADE]">
          <TabsTrigger value="all" data-testid="clients-all">Tous</TabsTrigger>
          <TabsTrigger value="active" data-testid="clients-active">Actifs</TabsTrigger>
          <TabsTrigger value="pending" data-testid="clients-pending">En attente</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#EBE5D9] rounded-xl py-20 text-center">
          <Users className="w-10 h-10 text-[#EBE5D9] mx-auto mb-3" />
          <p className="text-[#666]">Aucun client dans cette catégorie.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="bg-white border border-[#EBE5D9] rounded-xl p-6 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#5E2129]/8 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#5E2129]" />
                </div>
                {c.status === "pending" ? (
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#D97736]/10 text-[#D97736]">En attente</span>
                ) : (
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#2E4F36]/10 text-[#2E4F36]">Actif</span>
                )}
              </div>
              <p className="font-semibold text-[#1A1A1A]">{c.name}</p>
              <p className="text-sm text-[#666]">{c.company || "—"}</p>
              {c.email && <p className="text-sm text-[#666] mt-1">{c.email}</p>}
              {c.address && <p className="text-xs text-[#666] mt-1">{c.address}</p>}
              {c.status === "pending" && (
                <Button data-testid={`create-bexio-client-${c.id}`} onClick={() => createInBexio(c.id)} disabled={creatingId === c.id} className="mt-4 rounded-full bg-[#5E2129] hover:bg-[#4A1A20] text-white">
                  {creatingId === c.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />} Créer dans Bexio
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
